import { useEffect, useMemo, useRef } from "react";

type Props = {
  messages: string[];
  isAnalyzing: boolean;
  variant?: "light" | "dark";
};

type StepState = {
  key: "clean" | "insights" | "visualize" | "summarize" | "done";
  label: string;
  started: boolean;
  done: boolean;
  note?: string;
};

const CheckIcon = ({ className = "w-5 h-5 text-green-600" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const Spinner = ({ className = "w-5 h-5 text-blue-600" }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

export default function StatusPanel({ messages, variant = "dark" }: Props) {
  // const [showLogs, setShowLogs] = useState(false);
  const logsRef = useRef<HTMLDivElement>(null);

  // Auto-scroll raw logs
  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [messages]);

  // Parse messages into step states
  const steps = useMemo<StepState[]>(() => {
    const has = (s: string) => messages.some((m) => m.toLowerCase().includes(s.toLowerCase()));

    // Capture insights mode note (parallel/sequential) if present
    const insightsDoneMsg = messages.find((m) =>
      m.toLowerCase().includes("insights generation completed")
    );
    const insightsNote =
      insightsDoneMsg?.match(/\((parallel|sequential)\)/i)?.[0] ?? undefined;

    const s: StepState[] = [
      {
        key: "clean",
        label: "Data Cleaning",
        started: has("Data cleaning started"),
        done: has("Data cleaning completed"),
      },
      {
        key: "insights",
        label: "Generating insights",
        started: has("Generating insights"),
        done: has("Insights generation completed"),
        note: insightsNote,
      },
      {
        key: "visualize",
        label: "Visualization",
        started: has("Visualization started"),
        done: has("Visualization completed"),
      },
      {
        key: "summarize",
        label: "Summarizing insights",
        started: has("Summarizing insights"),
        done: has("Summarizing insights completed") || has("Pipeline finished"),
      },
      {
        key: "done",
        label: "Finished",
        started: has("Pipeline finished"),
        done: has("Pipeline finished"),
      },
    ];
    return s;
  }, [messages]);

  const completed = steps.filter((s) => s.done).length;
  const total = steps.length;
  const progress = Math.round((completed / total) * 100);

  const isDark = variant === "dark";

  return (
    <div className={`mt-4 rounded-xl ${isDark ? "bg-black border border-slate-800 text-slate-100 shadow-xl" : "bg-white border text-slate-800"}`}>
      <div className={`px-4 py-3 flex items-center justify-between ${isDark ? "border-b border-slate-800" : "border-b"}`}>
        <div className="flex items-center gap-2">
          {completed === total ? (
            <CheckIcon className={isDark ? "w-5 h-5 text-emerald-400" : "w-5 h-5 text-green-600"} />
          ) : (
            <Spinner className={isDark ? "w-5 h-5 text-indigo-400" : "w-5 h-5 text-blue-600"} />
          )}
          <div className="font-medium">
            {completed === total ? "Analysis complete" : "Analyzing…"}
          </div>
        </div>
        <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>{progress}%</div>
      </div>

      <div className="px-4 pt-3 pb-2">
        <div className={`w-full h-2 rounded ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
          <div
            className={`h-2 rounded ${isDark ? "bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400" : "bg-blue-600"}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <ol className="px-4 pb-3 space-y-3">
        {steps.map((step) => {
          const isActive = step.started && !step.done;
          const isDone = step.done;
          return (
            <li key={step.key} className="flex items-start gap-3">
              <div className="mt-0.5">
                {isDone ? (
                  <CheckIcon className={isDark ? "w-5 h-5 text-emerald-400" : "w-5 h-5 text-green-600"} />
                ) : isActive ? (
                  <Spinner className={isDark ? "w-5 h-5 text-indigo-400" : "w-5 h-5 text-blue-600"} />
                ) : (
                  <span className={`w-3 h-3 rounded-full inline-block ${isDark ? "bg-slate-700" : "bg-slate-300"}`} />
                )}
              </div>
              <div>
                <div
                  className={
                    isDone
                      ? isDark ? "text-slate-100 font-medium" : "text-slate-800 font-medium"
                      : isActive
                      ? isDark ? "text-indigo-300 font-medium" : "text-blue-700 font-medium"
                      : isDark ? "text-slate-400" : "text-slate-500"
                  }
                >
                  {step.label} {step.note ? <span className={isDark ? "text-slate-500 font-normal" : "text-slate-400 font-normal"}>{step.note}</span> : null}
                </div>
                {!isDone && isActive ? (
                  <div className={isDark ? "text-xs text-slate-500" : "text-xs text-slate-500"}>Running…</div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Raw logs section disabled per request */}
      {/*
      <div className={`px-4 py-3 ${isDark ? "border-t border-slate-800 bg-black/60" : "border-t bg-slate-50"}`}>
        <button
          type="button"
          className={`${isDark ? "text-xs text-slate-400 hover:text-slate-200" : "text-xs text-slate-600 hover:text-slate-800"} underline`}
          onClick={() => setShowLogs((s) => !s)}
        >
          {showLogs ? "Hide raw logs" : "Show raw logs"}
        </button>
        {showLogs && (
          <div
            ref={logsRef}
            className={`mt-2 max-h-40 overflow-y-auto text-xs border rounded p-2 ${isDark ? "bg-black/60 border-slate-800 text-slate-300" : "bg-white text-slate-700"}`}
            aria-live="polite"
          >
            {messages.length === 0 ? (
              <div className={isDark ? "text-slate-500" : "text-slate-400"}>No messages yet…</div>
            ) : (
              messages.map((m, i) => <div key={i}>{m}</div>)
            )}
            {isAnalyzing && <div className={isDark ? "text-indigo-300 animate-pulse" : "text-blue-500 animate-pulse"}>Processing…</div>}
          </div>
        )}
      </div>
      */}
    </div>
  );
}